import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Button } from './button';
import { FederationIcon, MandalaIcon, TreeOfLifeIcon } from './legendary-icons';
import { 
  Globe, Users, Leaf, Heart, Share2, MapPin,
  ChevronDown, ChevronRight, ExternalLink, Compass,
  Flame, TreePine, Wind, Sprout, Shield, BookOpen,
  Lightbulb, Target, Handshake, Home, Zap
} from 'lucide-react';

// Community Building Elements - extracted wisdom for the workflow
const COMMUNITY_ELEMENTS = {
  fireRoles: [
    {
      key: 'keeper',
      name: 'Fire Keepers',
      icon: Flame,
      color: '#D97706',
      essence: 'Those who regulate themselves under stress and carry the long-term vision',
      markers: [
        'Regulate themselves under stress',
        'Follow through without being chased',
        'Hold boundaries without drama',
        'Stay steady when others panic'
      ],
      wisdom: 'Revealed in crisis, not in circle.'
    },
    {
      key: 'sitter',
      name: 'Fire Sitters',
      icon: TreePine,
      color: '#059669',
      essence: 'The majority who need clear pathways to grow into responsibility',
      markers: [
        'Learn through small responsibilities',
        'Need clear roles and expectations',
        'Build conflict capacity over time',
        'Contribute to shared labor'
      ],
      wisdom: 'Become keepers through practice, not personality.'
    },
    {
      key: 'runner',
      name: 'Fire Runners',
      icon: Wind,
      color: '#6366F1',
      essence: 'Those who need structure, predictability, and mentorship to stabilize',
      markers: [
        'Need structure and predictability',
        'Benefit from clear boundaries',
        'Held by a system, not a person',
        'Can become powerful contributors when stabilized'
      ],
      wisdom: 'Stabilize them without collapsing trying to save them.'
    }
  ],
  
  buildingBlocks: [
    {
      key: 'initiation',
      name: 'Initiation Design',
      icon: BookOpen,
      color: '#8B5CF6',
      description: 'Orientation + responsibility + culture transmission',
      elements: ['Agreements', 'Expectations', 'Boundaries', 'Governance', 'Labor structure']
    },
    {
      key: 'governance',
      name: 'Governance Clarity',
      icon: Shield,
      color: '#2f6f3e',
      description: 'Not hierarchy — clarity',
      elements: ['Clear roles', 'Clear authority', 'Decision pathways', 'Conflict processes', 'Accountability']
    },
    {
      key: 'culture',
      name: 'Culture Protection',
      icon: Heart,
      color: '#DC2626',
      description: 'Culture is what people actually do',
      elements: ['Shared labor', 'Emotional regulation', 'Conflict literacy', 'Boundaries as care', 'No martyrdom']
    }
  ],

  partnershipLayers: [
    { stage: 'Ignition', description: 'Belonging felt', color: '#059669' },
    { stage: 'Incubation', description: 'Belonging stabilized into practice', color: '#D97706' },
    { stage: 'Continuation', description: 'Belonging becomes shared creation', color: '#8B5CF6' }
  ]
};

// Local federation data
const LOCAL_PODS = [
  { id: 1, name: 'Cedar Grove Family Pod', members: 7, stage: 'Bloom', focus: 'Land stewardship', bioregion: 'Pacific Northwest' },
  { id: 2, name: 'Mountain View Collective', members: 12, stage: 'Bud', focus: 'Food sovereignty', bioregion: 'Pacific Northwest' },
  { id: 3, name: 'River Song Community', members: 23, stage: 'Sprout', focus: 'Regenerative culture', bioregion: 'Pacific Northwest' },
  { id: 4, name: 'Desert Bloom Alliance', members: 15, stage: 'Seed', focus: 'Water commons', bioregion: 'Southwest' }
];

const STAGE_COLORS = {
  Seed: '#6a4b33',
  Sprout: '#4a7c59',
  Bud: '#b9935a',
  Bloom: '#2f6f3e'
};

const BIOREGION_STATS = {
  name: 'Pacific Northwest',
  communities: 12,
  pods: 47,
  activeProjects: 8
};

export const FederationView = ({ projectId }) => {
  const [activeTab, setActiveTab] = useState('pods');
  const [expandedRole, setExpandedRole] = useState(null);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [selectedPod, setSelectedPod] = useState(null);

  return (
    <div className="p-4 h-full overflow-y-auto" data-testid="federation-view">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <FederationIcon size={20} className="text-[var(--info)]" />
        <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
          Federation & Elements
        </h4>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-[var(--muted)]/30 rounded-xl mb-4">
        {[
          { key: 'pods', label: 'Local Pods', icon: Users },
          { key: 'elements', label: 'Elements', icon: Compass },
          { key: 'roles', label: 'Fire Roles', icon: Flame }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs transition-all ${
              activeTab === tab.key
                ? 'bg-[var(--card)] shadow-sm text-[var(--primary)]'
                : 'text-[var(--fg)]/50 hover:text-[var(--fg)]/70'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* PODS TAB */}
      {activeTab === 'pods' && (
        <div className="space-y-4">
          {/* Federation principle */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-[var(--info)]/10 to-[var(--primary)]/10 border border-[var(--info)]/20">
            <div className="flex items-center gap-2 mb-2">
              <Globe size={16} className="text-[var(--info)]" />
              <span className="text-xs font-medium text-[var(--fg)]">Planetary Family Network</span>
            </div>
            <p className="text-xs text-[var(--fg)]/60 italic">
              "Communities remain sovereign. Standards are shared. Surplus circulates. Power does not centralize."
            </p>
          </div>

          {/* Bioregion Stats */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <TreeOfLifeIcon size={18} className="text-[var(--primary)]" />
                <span className="text-sm font-medium text-[var(--fg)]">
                  {BIOREGION_STATS.name}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 rounded-lg bg-[var(--muted)]/30">
                  <p className="text-lg font-bold text-[var(--primary)]">{BIOREGION_STATS.communities}</p>
                  <p className="text-[10px] text-[var(--fg)]/50">Communities</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-[var(--muted)]/30">
                  <p className="text-lg font-bold text-[var(--accent)]">{BIOREGION_STATS.pods}</p>
                  <p className="text-[10px] text-[var(--fg)]/50">Family Pods</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-[var(--muted)]/30">
                  <p className="text-lg font-bold text-[var(--info)]">{BIOREGION_STATS.activeProjects}</p>
                  <p className="text-[10px] text-[var(--fg)]/50">Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pod List */}
          <div>
            <h5 className="text-xs font-semibold text-[var(--fg)]/70 mb-2 flex items-center gap-2">
              <MapPin size={14} />
              Aligned Pods
            </h5>
            <div className="space-y-2">
              {LOCAL_PODS.map((pod) => (
                <motion.div
                  key={pod.id}
                  whileHover={{ x: 4 }}
                  onClick={() => setSelectedPod(selectedPod?.id === pod.id ? null : pod)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${
                    selectedPod?.id === pod.id
                      ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                      : 'border-[var(--muted)] hover:border-[var(--primary)]/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[var(--fg)]">{pod.name}</span>
                    <Badge
                      className="text-[10px]"
                      style={{
                        backgroundColor: STAGE_COLORS[pod.stage] + '20',
                        color: STAGE_COLORS[pod.stage]
                      }}
                    >
                      {pod.stage}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[var(--fg)]/60">
                    <span className="flex items-center gap-1">
                      <Users size={10} />
                      {pod.members}
                    </span>
                    <span className="flex items-center gap-1">
                      <Leaf size={10} />
                      {pod.focus}
                    </span>
                  </div>

                  {selectedPod?.id === pod.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="mt-3 pt-3 border-t border-[var(--muted)] flex gap-2"
                    >
                      <button className="flex-1 text-xs py-2 rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors flex items-center justify-center gap-1">
                        <Heart size={12} />
                        Connect
                      </button>
                      <button className="flex-1 text-xs py-2 rounded-lg bg-[var(--accent)]/10 text-[var(--accent)] hover:bg-[var(--accent)]/20 transition-colors flex items-center justify-center gap-1">
                        <Share2 size={12} />
                        Share
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ELEMENTS TAB - Community Building Blocks */}
      {activeTab === 'elements' && (
        <div className="space-y-4">
          {/* Journey layers */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-[var(--primary)]/10 to-[var(--accent)]/10 border border-[var(--primary)]/20">
            <p className="text-xs font-medium text-[var(--fg)] mb-3 flex items-center gap-2">
              <Zap size={14} className="text-[var(--primary)]" />
              The Belonging Journey
            </p>
            <div className="space-y-2">
              {COMMUNITY_ELEMENTS.partnershipLayers.map((layer, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: layer.color }}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    <span className="text-xs font-medium text-[var(--fg)]">{layer.stage}</span>
                    <span className="text-xs text-[var(--fg)]/50 ml-2">— {layer.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Building Blocks */}
          <div>
            <h5 className="text-xs font-semibold text-[var(--fg)]/70 mb-2 flex items-center gap-2">
              <Target size={14} />
              Community Building Blocks
            </h5>
            <div className="space-y-2">
              {COMMUNITY_ELEMENTS.buildingBlocks.map((block) => (
                <motion.div key={block.key}>
                  <button
                    onClick={() => setExpandedBlock(expandedBlock === block.key ? null : block.key)}
                    className="w-full p-3 rounded-xl border border-[var(--muted)] hover:border-[var(--primary)]/30 transition-all text-left"
                    style={{ backgroundColor: expandedBlock === block.key ? block.color + '08' : 'transparent' }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: block.color + '20' }}
                      >
                        <block.icon size={20} style={{ color: block.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[var(--fg)]">{block.name}</p>
                        <p className="text-[10px] text-[var(--fg)]/50">{block.description}</p>
                      </div>
                      <ChevronRight 
                        size={14} 
                        className={`text-[var(--fg)]/30 transition-transform ${expandedBlock === block.key ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {expandedBlock === block.key && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div 
                          className="p-3 mt-1 rounded-lg border-l-4"
                          style={{ backgroundColor: block.color + '08', borderColor: block.color }}
                        >
                          <p className="text-xs text-[var(--fg)]/70 mb-2">Key elements:</p>
                          <div className="flex flex-wrap gap-1">
                            {block.elements.map((el, idx) => (
                              <span 
                                key={idx}
                                className="text-[10px] px-2 py-1 rounded-full"
                                style={{ backgroundColor: block.color + '15', color: block.color }}
                              >
                                {el}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Core principle */}
          <div className="p-3 rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20">
            <div className="flex items-start gap-2">
              <Lightbulb size={14} className="text-[var(--primary)] mt-0.5" />
              <div>
                <p className="text-xs font-medium text-[var(--primary)] mb-1">Remember</p>
                <p className="text-xs text-[var(--fg)]/60">
                  Without a Stewardship Core, the founder becomes the default core — and that always ends in burnout.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FIRE ROLES TAB */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          {/* Intro */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20">
            <p className="text-xs text-[var(--fg)]/70 italic text-center">
              "How do you build a community where the fire actually lasts?"
            </p>
          </div>

          {/* Fire Roles */}
          <div className="space-y-2">
            {COMMUNITY_ELEMENTS.fireRoles.map((role) => (
              <motion.div key={role.key}>
                <button
                  onClick={() => setExpandedRole(expandedRole === role.key ? null : role.key)}
                  className="w-full p-3 rounded-xl border transition-all text-left"
                  style={{ 
                    borderColor: expandedRole === role.key ? role.color : 'var(--muted)',
                    backgroundColor: expandedRole === role.key ? role.color + '08' : 'transparent'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: role.color + '20' }}
                    >
                      <role.icon size={24} style={{ color: role.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: role.color }}>{role.name}</p>
                      <p className="text-xs text-[var(--fg)]/50">{role.essence}</p>
                    </div>
                    <ChevronDown 
                      size={14} 
                      className={`text-[var(--fg)]/30 transition-transform ${expandedRole === role.key ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
                
                <AnimatePresence>
                  {expandedRole === role.key && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div 
                        className="p-4 mt-1 rounded-xl border-l-4"
                        style={{ backgroundColor: role.color + '05', borderColor: role.color }}
                      >
                        <p className="text-xs font-medium text-[var(--fg)]/70 mb-2">Markers:</p>
                        <ul className="space-y-1.5 mb-3">
                          {role.markers.map((marker, idx) => (
                            <li key={idx} className="text-xs text-[var(--fg)]/60 flex items-start gap-2">
                              <span style={{ color: role.color }}>•</span>
                              {marker}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs italic" style={{ color: role.color }}>
                          "{role.wisdom}"
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Stewardship Core */}
          <Card className="border-[var(--primary)]/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/20 flex items-center justify-center">
                  <Sprout size={20} className="text-[var(--primary)]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--primary)]">Stewardship Core</p>
                  <p className="text-[10px] text-[var(--fg)]/50">The backbone of any lasting community</p>
                </div>
              </div>
              <div className="space-y-1 text-xs text-[var(--fg)]/60">
                <p>• Multiple Fire Keepers</p>
                <p>• A few advanced Fire Sitters</p>
                <p>• No one who is actively running</p>
              </div>
              <div className="mt-3 p-2 rounded-lg bg-[var(--primary)]/10">
                <p className="text-xs text-[var(--primary)] font-medium">
                  This core sets the culture, holds boundaries, trains new members, and carries the long-term vision.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Culture reminder */}
          <div className="p-3 rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20">
            <div className="flex items-start gap-2">
              <MandalaIcon size={18} className="text-[var(--primary)]" />
              <div>
                <p className="text-xs font-medium text-[var(--primary)] mb-1">The Goal</p>
                <p className="text-xs text-[var(--fg)]/60">
                  This is how a community becomes a village — where Stewards are supported, Sitters are guided, 
                  Runners are stabilized, and the fire is held by the system, not the founder.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FederationView;
